// src/components/CampaignSuggestions.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Badge,
  Divider,
  Flex,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import { getRoiBadge, getTrendIcon } from "./flyer/roiUtils";

/**
 * CampaignSuggestions
 * Displays AI-driven campaign suggestions for the current business.
 * - Calls RPC get_campaign_suggestions with integer businessId.
 * - Shows postcode sector, reason, ROI estimate, and trend vs previous 14 days.
 */
export default function CampaignSuggestions({ businessId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!businessId) return;

      setLoading(true);
      try {
        const { data, error } = await supabase.rpc("get_campaign_suggestions", {
          business_id: businessId, // ✅ integer ID
        });

        if (error) throw error;
        setSuggestions(data || []);
      } catch (err) {
        console.error("Failed to fetch campaign suggestions:", err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [businessId]);

  return (
    <Box p={4} bg="gray.50" borderRadius="md">
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold">🧠 AI Campaign Suggestions</Text>
        <Badge colorScheme="purple" variant="subtle">
          RPC: get_campaign_suggestions
        </Badge>
      </Flex>

      {loading ? (
        <Spinner />
      ) : suggestions.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No suggestions available.
        </Text>
      ) : (
        <VStack align="start" spacing={3}>
          {suggestions.map((s) => {
            const roiBadge = getRoiBadge(s.roi_estimate || 0);
            const trendIcon = getTrendIcon(
              s.roi_estimate || 0,
              s.roi_estimate_prev_14d || 0
            );

            return (
              <Box key={s.postcode_sector}>
                <Text fontSize="sm">
                  Target <strong>{s.postcode_sector || "Unknown Sector"}</strong>{" "}
                  — {s.reason || "No reason provided"}
                  <Badge ml={2} backgroundColor={roiBadge.color} color="white">
                    {trendIcon} {roiBadge.label}
                  </Badge>
                </Text>
                <Divider />
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
